import { useEffect, useMemo, useState } from 'react';
import { getAudit, type AuditEntry } from './admin-api';
import { downloadCSV, type Col } from './export-utils';

// Человекочитаемые названия действий + цвет метки (класс chip).
const ACTIONS: Record<string, { label: string; cls: string }> = {
  login: { label: 'Вход в систему', cls: 'grn' },
  login_failed: { label: 'Неудачный вход', cls: 'red' },
  pay_marked: { label: 'Оплата отмечена', cls: 'grn' },
  pay_unmarked: { label: 'Оплата снята', cls: 'gold' },
  deal_advance: { label: 'Аванс проведён', cls: 'grn' },
  deal_stage: { label: 'Смена этапа сделки', cls: 'cy' },
  deal_archive: { label: 'Сделка в архив', cls: 'gold' },
  deal_restore: { label: 'Сделка из архива', cls: 'grn' },
  kpp_pass: { label: 'Выпуск машины (КПП)', cls: 'grn' },
  doc_generated: { label: 'Документ сформирован', cls: 'cy' },
  user_create: { label: 'Создан пользователь', cls: 'vi' },
  user_update: { label: 'Изменён пользователь', cls: 'gold' },
  access_grant: { label: 'Доступ выдан', cls: 'grn' },
  access_revoke: { label: 'Доступ снят', cls: 'red' },
  role_create: { label: 'Создана роль', cls: 'vi' },
  role_delete: { label: 'Удалена роль', cls: 'red' },
  role_flags: { label: 'Флаги роли изменены', cls: 'gold' },
  preset_create: { label: 'Создан пресет платежей', cls: 'vi' },
  preset_update: { label: 'Изменён пресет платежей', cls: 'gold' },
  preset_delete: { label: 'Удалён пресет платежей', cls: 'red' },
  stage_add: { label: 'Добавлен этап', cls: 'vi' },
  stage_rename: { label: 'Переименован этап', cls: 'gold' },
  stage_delete: { label: 'Удалён этап', cls: 'red' },
  integration_save: { label: 'Настройка интеграции', cls: 'gold' },
  backup: { label: 'Резервная копия БД', cls: 'cy' },
};

const ENTITIES: Record<string, string> = {
  deal: 'сделка',
  user: 'пользователь',
  role: 'роль',
  pay_preset: 'пресет',
  stage: 'этап',
  integration: 'интеграция',
  kpp: 'КПП',
  db: 'БД',
};

const actionLabel = (a: string) => ACTIONS[a]?.label ?? a;
const actionCls = (a: string) => ACTIONS[a]?.cls ?? '';
const entityLabel = (e: string | null) => (e ? (ENTITIES[e] ?? e) : '');

// Метка времени хранится в UTC (datetime('now')); показываем во времени завода (Asia/Almaty).
function fmtTime(at: string): string {
  if (!at) return '';
  const iso = at.includes('T') ? at : at.replace(' ', 'T') + (at.endsWith('Z') ? '' : 'Z');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString('ru-RU', {
    timeZone: 'Asia/Almaty',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const objectText = (e: AuditEntry) =>
  [entityLabel(e.entity), e.entity_id ? `#${e.entity_id}` : ''].filter(Boolean).join(' ');

// Журнал аудита: кто, что и над чем сделал. Только чтение.
export function AuditPanel() {
  const [rows, setRows] = useState<AuditEntry[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState('');
  const [limit, setLimit] = useState(100);
  const [q, setQ] = useState('');

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const r = await getAudit({ limit, action: action || undefined });
      setRows(r.log);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось загрузить журнал.');
    } finally {
      setBusy(false);
    }
  }

  // перезагружаем при смене серверных фильтров (действие/лимит)
  useEffect(() => {
    load();
  }, [action, limit]);

  // Набор действий для выпадающего списка — из того, что реально встречается + известные.
  const actionOptions = useMemo(() => {
    const seen = new Set<string>(Object.keys(ACTIONS));
    (rows ?? []).forEach((r) => seen.add(r.action));
    return [...seen].sort((a, b) => actionLabel(a).localeCompare(actionLabel(b), 'ru'));
  }, [rows]);

  // Локальный текстовый фильтр — по кто/детали/объекту.
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle || !rows) return rows ?? [];
    return rows.filter((r) =>
      [r.user_name, r.detail, actionLabel(r.action), objectText(r)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [rows, q]);

  function exportCSV() {
    const cols: Col<AuditEntry>[] = [
      { header: 'Время', value: (r) => fmtTime(r.at) },
      { header: 'Кто', value: (r) => r.user_name ?? '—' },
      { header: 'Действие', value: (r) => actionLabel(r.action) },
      { header: 'Объект', value: (r) => objectText(r) },
      { header: 'Детали', value: (r) => r.detail ?? '' },
    ];
    downloadCSV('audit', cols, shown);
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <p className="muted small grow" style={{ margin: 0, minWidth: 180 }}>
          Кто и что сделал в системе. Записи снимаются автоматически.
        </p>
        <select value={action} disabled={busy} onChange={(e) => setAction(e.target.value)}>
          <option value="">Все действия</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>
              {actionLabel(a)}
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Поиск: кто / детали"
          style={{ width: 180 }}
        />
        <select value={limit} disabled={busy} onChange={(e) => setLimit(Number(e.target.value))}>
          <option value={100}>100 записей</option>
          <option value={250}>250 записей</option>
          <option value={500}>500 записей</option>
        </select>
        <button className="btn ghost sm" disabled={busy} onClick={() => load()}>
          Обновить
        </button>
        <button className="btn ghost sm" disabled={busy || shown.length === 0} onClick={exportCSV}>
          CSV
        </button>
      </div>

      {err && <p className="err">{err}</p>}
      {!rows && !err && <p className="muted">Загрузка…</p>}

      {rows && (
        <div className="tablewrap glass">
          <table className="tbl card-table">
            <thead>
              <tr>
                <th>Время</th>
                <th>Кто</th>
                <th>Действие</th>
                <th>Объект</th>
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} style={{ cursor: 'default' }}>
                  <td data-label="Время" className="mono muted" style={{ whiteSpace: 'nowrap' }}>
                    {fmtTime(r.at)}
                  </td>
                  <td data-label="Кто">{r.user_name ?? <span className="muted">—</span>}</td>
                  <td data-label="Действие">
                    <span className={'chip ' + actionCls(r.action)}>{actionLabel(r.action)}</span>
                  </td>
                  <td data-label="Объект" className="muted">
                    {objectText(r) || '—'}
                  </td>
                  <td data-label="Детали" className="small">
                    {r.detail ?? ''}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    {q.trim() ? 'Ничего не найдено по запросу.' : 'Записей пока нет.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
